import { BookOpenText, MapPin, Search, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Sheet } from '../../ui/Sheet';
import { WIKI_ARTICLES, WIKI_CATEGORIES, type WikiArticle, type WikiBlock, type WikiCategory } from './articles';

/** **bold** in article text. */
function rich(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>));
}
const plain = (text: string) => text.replace(/\*\*/g, '');
const blockText = (b: WikiBlock) => ('p' in b ? b.p : 'list' in b ? b.list.join(' ') : b.steps.join(' '));
const searchText = (a: WikiArticle) => plain([a.title, a.summary, a.where ?? '', ...(a.keywords ?? []), ...a.body.map(blockText)].join(' ')).toLowerCase();

/** How well an article matches every word of the search: title hits first, then keywords, then the text. 0 = no match. */
function score(a: WikiArticle, words: string[]): number {
  const all = searchText(a);
  if (!words.every((w) => all.includes(w))) return 0;
  const title = a.title.toLowerCase();
  const keys = (a.keywords ?? []).join(' ').toLowerCase();
  return words.reduce((n, w) => n + (title.includes(w) ? 10 : keys.includes(w) ? 5 : 1), 0);
}

/**
 * The wiki (#536): every option, what it does and how it works, with screenshots. Search and category filters; each
 * article has a stable link (/wiki#id) so Help and other pages can point straight at one.
 */
export function Wiki() {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<WikiCategory | 'all'>('all');
  const [zoom, setZoom] = useState<{ src: string; caption: string } | null>(null);

  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = useMemo(() => {
    const inCategory = WIKI_ARTICLES.filter((a) => category === 'all' || a.category === category);
    if (!words.length) return inCategory;
    return inCategory
      .map((a) => ({ a, s: score(a, words) }))
      .filter((x) => x.s > 0)
      .sort((x, y) => y.s - x.s)
      .map((x) => x.a);
  }, [category, words.join(' ')]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const a of WIKI_ARTICLES) if (!words.length || score(a, words) > 0) c[a.category] = (c[a.category] ?? 0) + 1;
    return c;
  }, [words.join(' ')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the article the address names, even if a filter would hide it. A word instead of an id (say #passkeys)
  // opens the article that covers it.
  const hash = decodeURIComponent(location.hash.slice(1)).toLowerCase();
  const target =
    WIKI_ARTICLES.find((a) => a.id === hash)?.id ??
    (hash ? WIKI_ARTICLES.find((a) => (a.keywords ?? []).some((k) => k === hash || k === hash.replace(/s$/, '')))?.id : undefined) ??
    '';
  useEffect(() => {
    if (!target || !WIKI_ARTICLES.some((a) => a.id === target)) return;
    if (!shown.some((a) => a.id === target)) {
      setQuery('');
      setCategory('all');
      return;
    }
    const go = () => document.getElementById(`wiki-${target}`)?.scrollIntoView({ block: 'start' });
    requestAnimationFrame(go);
    const again = setTimeout(go, 400); // once fonts and the contents list have settled
    return () => clearTimeout(again);
  }, [target, shown]);

  const titleOf = (id: string) => WIKI_ARTICLES.find((a) => a.id === id)?.title ?? id;
  const grouped = WIKI_CATEGORIES.map((c) => ({ ...c, articles: shown.filter((a) => a.category === c.id) })).filter((g) => g.articles.length);

  return (
    <div className="page wiki-page">
      <div className="page-head">
        <div>
          <h1>
            <BookOpenText size={24} className="inline-icon" /> ClassGuardian guide
          </h1>
          <p className="muted">Every option, what it does and how it works. Search, or pick a topic.</p>
        </div>
      </div>

      <div className="wiki-tools">
        <label className="wiki-search">
          <Search size={18} aria-hidden />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the guide (try “holidays”, “passkey” or “block YouTube”)" aria-label="Search the guide" />
          {query && (
            <button className="icon-btn" onClick={() => setQuery('')} aria-label="Clear search">
              <X size={16} />
            </button>
          )}
        </label>
        <div className="chips wiki-chips" role="group" aria-label="Topics">
          <button className={`chip ${category === 'all' ? 'active' : ''}`} onClick={() => setCategory('all')} aria-pressed={category === 'all'}>
            All <span className="muted">{Object.values(counts).reduce((a, b) => a + b, 0)}</span>
          </button>
          {WIKI_CATEGORIES.map((c) => (
            <button key={c.id} className={`chip ${category === c.id ? 'active' : ''}`} onClick={() => setCategory(c.id)} aria-pressed={category === c.id} disabled={!counts[c.id]}>
              {c.label} <span className="muted">{counts[c.id] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {!shown.length ? (
        <p className="muted">Nothing matches “{query.trim()}”. Try another word, or <Link to="/feedback">ask us</Link>.</p>
      ) : (
        <div className="wiki-layout">
          <nav className="wiki-toc card" aria-label="Articles">
            {grouped.map((g) => (
              <div key={g.id} className="wiki-toc-group">
                <div className="settings-group">{g.label}</div>
                {g.articles.map((a) => (
                  <a key={a.id} href={`#${a.id}`} className={target === a.id ? 'active' : ''}>
                    {a.title}
                  </a>
                ))}
              </div>
            ))}
          </nav>

          <div className="wiki-articles">
            {grouped.map((g) => (
              <Fragment key={g.id}>
                <h2 className="settings-group">{g.label}</h2>
                {g.articles.map((a) => (
                  <article key={a.id} id={`wiki-${a.id}`} className={`card stack wiki-article ${target === a.id ? 'targeted' : ''}`}>
                    <div>
                      <h3>
                        <a href={`#${a.id}`} className="wiki-anchor">
                          {a.title}
                        </a>
                      </h3>
                      <p className="muted">{a.summary}</p>
                    </div>
                    {a.where && (
                      <p className="small wiki-where">
                        <MapPin size={14} aria-hidden /> <span>{rich(a.where)}</span>
                      </p>
                    )}
                    {a.body.map((b, i) =>
                      'p' in b ? (
                        <p key={i}>{rich(b.p)}</p>
                      ) : 'list' in b ? (
                        <ul key={i} className="help-list">
                          {b.list.map((t, j) => (
                            <li key={j}>{rich(t)}</li>
                          ))}
                        </ul>
                      ) : (
                        <ol key={i} className="wiki-steps">
                          {b.steps.map((t, j) => (
                            <li key={j}>{rich(t)}</li>
                          ))}
                        </ol>
                      ),
                    )}
                    {!!a.shots?.length && (
                      <div className="wiki-shots">
                        {a.shots.map((s) => (
                          <figure key={s.src}>
                            <button className="wiki-shot" onClick={() => setZoom(s)} aria-label={`Enlarge: ${s.caption}`}>
                              <img src={s.src} alt={s.caption} loading="lazy" />
                            </button>
                            <figcaption className="muted small">{s.caption}</figcaption>
                          </figure>
                        ))}
                      </div>
                    )}
                    {!!a.related?.length && (
                      <p className="small wiki-related">
                        See also:{' '}
                        {a.related.map((id, i) => (
                          <Fragment key={id}>
                            {i > 0 && ' · '}
                            <a href={`#${id}`}>{titleOf(id)}</a>
                          </Fragment>
                        ))}
                      </p>
                    )}
                  </article>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      )}

      {zoom && (
        <Sheet title={zoom.caption} onClose={() => setZoom(null)}>
          <img src={zoom.src} alt={zoom.caption} className="wiki-zoom" />
        </Sheet>
      )}
    </div>
  );
}
